import { Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { isEmpty } from "lodash";
import React, { memo } from "react";
import { useHistory } from "react-router-dom";
import { Event, Timeslot } from "types";
import { getListViewColor } from "../Colors/colorHelpers";
import { SidebarColor } from "../Colors/SidebarColor";
import { BlackoutTimeslotTooltip } from "../Common/BlackoutTimeslotTooltip";
import { DashboardPlaceholderItem } from "../DashboardPlaceholderItem";
import { updateTimeslotsCollectionWithPlaceholder } from "../helper";
import { DashboardItemTime } from "../Time";
import { CollapsedTimeslotDisplay } from "./CollapsedTimeslotDisplay";
import { BasicTimeslotDisplay, ExpandedTimeslotDisplay } from "./ExpandedTimeslotDisplay";
import { ProductImage } from "@/components/Common/ProductImage";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useExperience } from "@/hooks/resources/useProduct";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useResource } from "@/hooks/useResource";
import { canOpenRoster, getId } from "@/models/Timeslot";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { useRosterDrawerStore } from "@/stores/rosterDrawerStore";
import { DashboardColorPreference, DashboardMode } from "@/types/Dashboard";

interface DashboardItemProps {
    timeslot: Timeslot;
    className?: string;
    isCollapsed?: boolean;
    isSelected?: boolean;
    isVirtualized: boolean;
}

export const DashboardItem = memo<DashboardItemProps>(
    ({ timeslot, isCollapsed = false, isVirtualized = false, isSelected = false, className, ...rest }) => {
        const history = useHistory();
        const mode = useDashboardFilterStore((state) => state.filters.mode);
        const { data: preferences } = useInfiniteCollection<DashboardColorPreference>("preferences");

        const { experience } = useExperience(timeslot.product);
        const { data: event } = useResource<Event>(["events", timeslot.event?.id]);

        const setDrawerOpen = useRosterDrawerStore((state) => state.setOpen);
        const setTimeslot = useRosterDrawerStore((state) => state.setTimeslot);

        const isRosterAvailable = canOpenRoster(timeslot);

        const handleClick = () => {
            if (!isRosterAvailable) {
                return;
            }

            if (mode && mode !== DashboardMode.LIST) {
                // Only for day, week, month when invoked via DashboardCalendarItemContent
                setTimeslot(timeslot);
                setDrawerOpen();
            } else {
                // A specific slot was selected, open it
                history.push(`/dashboard/${timeslot.id ?? getId(timeslot)}`);
            }
        };

        const handleBeforePlaceholderClick = async () => {
            if (timeslot.placeholder) {
                updateTimeslotsCollectionWithPlaceholder(timeslot, timeslot.placeholder, "before");
            }
        };

        const handleAfterPlaceholderClick = async () => {
            if (timeslot.placeholder) {
                updateTimeslotsCollectionWithPlaceholder(timeslot, timeslot.placeholder, "after");
            }
        };

        const rowProps = preferences.length > 0 ? getListViewColor(timeslot.product, timeslot) ?? {} : {};

        return (
            <div>
                {!isEmpty(timeslot.placeholder?.before) && (
                    <ErrorBoundary fallbackMessage="Error dashboard item placeholder">
                        <DashboardPlaceholderItem onClick={handleBeforePlaceholderClick} />
                    </ErrorBoundary>
                )}

                <BlackoutTimeslotTooltip reason={isRosterAvailable ? undefined : timeslot.unavailability?.message}>
                    <div
                        data-event-id={event?.id}
                        className={clsx(
                            "ui-dashboard-item flex overflow-hidden border-b border-gray-lighter p-1.5",
                            isRosterAvailable ? "hover:cursor-pointer" : "hover:cursor-not-allowed",
                            isSelected && "bg-primary-lighter",
                            className,
                        )}
                        onClick={handleClick}
                        {...rest}
                    >
                        <div
                            data-testid="dashboard-item-inner"
                            className={clsx(
                                "flex w-full rounded border border-transparent py-1.5 px-2",
                                isCollapsed ? "pr-0" : "pr-8",
                            )}
                            style={rowProps.style} // The colors from custom view XS-935
                        >
                            <SidebarColor
                                isCollapsed={isCollapsed}
                                productId={timeslot.product}
                                capacity={timeslot.capacity}
                                className="mr-2.5"
                            />

                            <div className={clsx("flex-shrink-0", isCollapsed ? "" : "mr-3")}>
                                {timeslot.time !== null && (
                                    <ErrorBoundary fallbackMessage="Error dashboard item time">
                                        <DashboardItemTime
                                            dateTime={timeslot.arrivalDatetime}
                                            time={timeslot.time}
                                            event={event}
                                        />
                                    </ErrorBoundary>
                                )}
                            </div>

                            {experience ? (
                                <ErrorBoundary fallbackMessage="Error dashboard product image">
                                    <ProductImage product={experience} className={clsx(isCollapsed && "!mr-1")} />
                                </ErrorBoundary>
                            ) : (
                                <div className="mt-1 mr-3 flex-shrink-0">
                                    <Skeleton height={56}>
                                        <div className="h-14 w-14" />
                                    </Skeleton>
                                </div>
                            )}

                            {isCollapsed ? (
                                <ErrorBoundary fallbackMessage="Error dashboard collapsed timeslot display">
                                    <CollapsedTimeslotDisplay timeslot={timeslot} />
                                </ErrorBoundary>
                            ) : isVirtualized ? (
                                <ExpandedTimeslotDisplay timeslot={timeslot} />
                            ) : (
                                <ErrorBoundary fallbackMessage="Error dashboard basic timeslot display">
                                    <BasicTimeslotDisplay timeslot={timeslot} />
                                </ErrorBoundary>
                            )}
                        </div>
                    </div>
                </BlackoutTimeslotTooltip>

                {!isEmpty(timeslot.placeholder?.after) && (
                    <ErrorBoundary fallbackMessage="Error dashboard item placeholder">
                        <DashboardPlaceholderItem onClick={handleAfterPlaceholderClick} />
                    </ErrorBoundary>
                )}
            </div>
        );
    },
);
